import {
  selectPointCloudStreamIdsForEntities,
  selectVisibilityMap,
} from "@/data-preparation-tool/store/data-preparation-ui/data-preparation-ui-selectors";
import { PointCloudObject } from "@/object-cache";
import { Features } from "@/store/features/features";
import { selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import { RevisionScanEntity } from "@faro-lotv/service-wires";
import { useFrame } from "@react-three/fiber";
import { throttle } from "es-toolkit";
import { useMemo, type JSX } from "react";
import { shallowEqual } from "react-redux";
import { calculateNodeStats } from "./point-cloud-stats-collector";
import { calculatePointOpacity } from "./point-opacity-collector";
import {
  RenderingStatsContext,
  useRenderingStatsContext,
} from "./rendering-stats-context";

type RenderingStatsCollectorProps = {
  scanEntities: RevisionScanEntity[];
  pointCloudObjects: PointCloudObject[];
};

/**
 * Collect rendering statistics for point clouds.
 * This is only executed when the feature flag `RenderingDebug` is enabled.
 * Must be placed inside a `Canvas` component.
 *
 * @param props Props to pass to the collector.
 * @returns Nothing to be rendered, just collects stats.
 */
export function RenderingStatsCollector(
  props: RenderingStatsCollectorProps,
): JSX.Element | null {
  // Collect stats only when the rendering debug overlay is enabled
  const isEnabled = useAppSelector(selectHasFeature(Features.RenderingDebug));

  if (!isEnabled) {
    return null;
  }

  return <InnerRenderingStatsCollector {...props} />;
}

/**
 * @returns The inner component that actually collects the stats.
 *  Separated so the hooks are only executed when the collector is actually rendered.
 */
function InnerRenderingStatsCollector({
  scanEntities,
  pointCloudObjects,
}: RenderingStatsCollectorProps): JSX.Element | null {
  const visibilityMap = useAppSelector(selectVisibilityMap);
  const visibleScanEntities = useMemo(
    () => scanEntities.filter((scan) => visibilityMap[scan.id] !== false),
    [scanEntities, visibilityMap],
  );
  const visiblePointCloudIds = useAppSelector(
    selectPointCloudStreamIdsForEntities(visibleScanEntities),
    shallowEqual,
  );
  const visiblePointClouds = useMemo(
    () =>
      pointCloudObjects.filter((pc) =>
        visiblePointCloudIds.includes(pc.iElement.id),
      ),
    [visiblePointCloudIds, pointCloudObjects],
  );

  const statsContext = useRenderingStatsContext();

  useFrame(() => {
    collectStats(statsContext, visiblePointClouds);
  });

  return null;
}

const COLLECTION_DELAY_MS = 500;

const collectStats = throttle(
  (
    statsContext: RenderingStatsContext,
    visiblePointClouds: PointCloudObject[],
  ) => {
    statsContext.setPointCloudStatsMap(calculateNodeStats(visiblePointClouds));
    statsContext.setPointOpacityMap(calculatePointOpacity(visiblePointClouds));
  },
  COLLECTION_DELAY_MS,
);
